import styled, { css } from 'styled-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  AI_DARK_GRAY,
  AI_GRADIENT,
  AI_GRAY,
  AI_META_CARD_BORDER,
  AI_PINK,
  AI_POSITIVE_GREEN,
  AI_TERTIARY_TEXT,
} from './aiEvaluationTokens'

export const PreviewLayout = styled.div`
  display: flex;
  flex-direction: column;
  max-height: min(72vh, 640px);
  min-height: 320px;
  font-family: Roboto, sans-serif;
`

export const PreviewScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0;
  margin-right: 2px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d9d9d9;
    border-radius: 4px;
  }
`

export const PreviewHeaderCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

export const PreviewIntro = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 24px;
  color: ${AI_DARK_GRAY};

  strong {
    font-weight: 700;
  }
`

export const PreviewSummary = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 24px;
  color: ${AI_DARK_GRAY};

  strong {
    font-weight: 700;
  }
`

export const PreviewAlertWrap = styled.div`
  margin: 16px 0 0;

  .ant-alert {
    border-radius: 8px;
    font-family: Roboto, sans-serif;
    font-size: 14px;
  }
`

export const MetaCard = styled.div`
  margin-top: 24px;
  padding: 24px;
  border: 1px solid ${AI_META_CARD_BORDER};
  border-radius: 16px;
  background: #ffffff;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  gap: 16px;
`

export const MetaField = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

export const MetaLabel = styled.span`
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
  color: ${AI_GRAY};
  margin-bottom: 0;
`

export const MetaValueRow = styled.div`
  display: flex;
  align-items: center;
  min-height: 40px;
  padding: 10px 10px 10px 0;
  border-bottom: 1px solid #d9d9d9;
`

export const MetaValue = styled.p<{ $muted?: boolean }>`
  margin: 0;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: ${({ $muted }) => ($muted ? AI_GRAY : AI_DARK_GRAY)};
  word-break: break-word;
`

export const BlockGroupsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 24px;
  margin-bottom: 35px;
`

export const BlockCard = styled.div`
  border: 1px solid ${AI_META_CARD_BORDER};
  border-radius: 16px;
  padding: 24px;
  background: #ffffff;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
`

export const BlockTag = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-bottom: 16px;
  padding: 4px;
  border-radius: 8px;
  border: 1px solid ${AI_PINK};
  background: #ffffff;
  font-size: 14px;
  line-height: 20px;
  color: ${AI_DARK_GRAY};

  strong {
    font-weight: 700;
  }
`

export const BlockTagText = styled.span`
  text-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);
`

export const BlockTagSparkle = styled(FontAwesomeIcon)`
  width: 16px !important;
  height: 16px !important;
  flex-shrink: 0;

  path {
    fill: url(#rankmiAiTagSparkleGradient);
  }
`

export const QuestionItem = styled.div<{ $withDivider?: boolean }>`
  ${({ $withDivider }) =>
    $withDivider &&
    css`
      margin-top: 16px;
      padding-top: 16px;
      border-top: 1px solid #f0f0f0;
    `}
`

export const QuestionTitle = styled.p`
  margin: 0 0 16px;
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  color: ${AI_TERTIARY_TEXT};
`

export const OptionsList = styled.ul`
  list-style: none;
  margin: 0 0 16px;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
`

export const QuestionItemRegenerating = styled.div`
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;

  .ant-spin-text {
    color: ${AI_GRAY};
    font-family: Roboto, sans-serif;
    font-size: 14px;
  }

  .ant-spin-dot-item {
    background-color: #0669f5;
  }
`

export const OptionRow = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 40px;
  padding: 8px 12px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  background: #fafafa;
  font-size: 14px;
  line-height: 20px;
  color: ${AI_DARK_GRAY};
`

export const OptionLabel = styled.span`
  flex: 1;
  min-width: 0;
`

export const CorrectAnswerIcon = styled(FontAwesomeIcon)`
  flex-shrink: 0;
  width: 16px !important;
  height: 16px !important;
  color: ${AI_POSITIVE_GREEN} !important;
  cursor: help;
`

export const QuestionActions = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  align-items: center;
`

const questionButtonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 12px;
  border-radius: 8px;
  font-family: Roboto, sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 16px;
  cursor: pointer;
  box-sizing: border-box;
  transition: box-shadow 0.15s ease, opacity 0.15s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }
`

const gradientText = css`
  background: ${AI_GRADIENT};
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
`

export const DeleteQuestionButton = styled.button`
  ${questionButtonBase}
  border: none;
  background: #f0f0f0;
  color: #4c4c4c;

  &:hover:not(:disabled) {
    background: #e8e8e8;
  }
`

export const RegenerateQuestionButton = styled.button`
  ${questionButtonBase}
  border: 1px solid ${AI_PINK};
  background: #ffffff;

  &:hover:not(:disabled) {
    box-shadow: 0 2px 8px rgba(255, 93, 44, 0.16);
  }
`

export const RegenerateQuestionIcon = styled(FontAwesomeIcon)`
  width: 16px !important;
  height: 16px !important;
  flex-shrink: 0;

  path {
    fill: url(#rankmiAiGradient);
  }
`

export const RegenerateQuestionLabel = styled.span`
  ${gradientText}
`

export const PreviewFooter = styled.div`
  flex-shrink: 0;
  margin: 0 -24px -24px;
  padding: 24px 24px 24px;
  border-top: 1px solid #f0f0f0;
  background: #ffffff;
`

export const FooterActions = styled.div`
  display: flex;
  gap: 16px;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
`

const footerButtonBase = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 40px;
  padding: 12px 16px;
  border-radius: 8px;
  font-family: Roboto, sans-serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 16px;
  cursor: pointer;
  box-sizing: border-box;
  transition: box-shadow 0.15s ease, opacity 0.15s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    pointer-events: none;
  }
`

export const FooterCancelButton = styled.button`
  ${footerButtonBase}
  border: none;
  background: #f0f0f0;
  color: #4c4c4c;

  &:hover:not(:disabled) {
    background: #e8e8e8;
  }
`

export const FooterRegenerateButton = styled.button`
  ${footerButtonBase}
  border: 1px solid ${AI_PINK};
  background: #ffffff;
  color: transparent;

  &:hover:not(:disabled) {
    box-shadow: 0 2px 8px rgba(255, 93, 44, 0.16);
  }
`

export const FooterRegenerateIcon = styled(FontAwesomeIcon)`
  width: 16px !important;
  height: 16px !important;
  flex-shrink: 0;

  path {
    fill: url(#rankmiAiGradient);
  }
`

export const FooterRegenerateLabel = styled.span`
  ${gradientText}
`

export const FooterDisclaimer = styled.p`
  margin: 16px 0 0;
  font-size: 12px;
  line-height: 16px;
  color: ${AI_GRAY};
  text-align: center;
`

export const SaveAndConfigureButton = styled.button<{ $disabled?: boolean }>`
  ${footerButtonBase}
  border: none;
  padding: 0 16px;
  background: ${AI_GRADIENT};
  color: #ffffff;
  box-shadow: 0 4px 4px rgba(0, 0, 0, 0.08);
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? 'none' : 'auto')};

  &:hover:not(:disabled) {
    box-shadow: 0 4px 12px rgba(255, 93, 44, 0.28);
  }
`

export const SaveButtonIcon = styled(FontAwesomeIcon)`
  width: 16px !important;
  height: 16px !important;
  color: #ffffff !important;
`
